import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, App, Button, Tag } from 'antd';
import { produce } from 'immer';
import _ from 'lodash';

import { api } from '../../api/client';
import { API_COLLECTIONS_URL, API_ELEMENT_SETS_URL, API_ELEMENT_SET_PROMOTE_URL, API_ELEMENT_SET_URL, isAdmin } from '../../utils';
import { canManage } from '../../utils/permissions';
import { normalizeId } from '../../utils/ids';
import { elementSetKeyFromName, indexSavedElements, namespacedElementNames } from '../../utils/conversions';
import { SHAPE_INSTRUCTION_KEY, dropStaleShapeInstructions, mergeConversionAnswers, shapeRefusalFor } from '../../utils/shapeChange';
import { withInertChoicesAsStored } from '../../utils/choiceValues';
import MultiToggleDialog from '../cards/MultiToggleDialog';
import { CARDS_CRUMB, collectionCrumbs } from '../../utils/breadcrumbs';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { useScreenShortcuts } from '../../contexts/ShortcutsContext';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { InputIconSelect, InputText } from '../Input';
import ContextBar, { ContextBarTabs } from '../common/ContextBar';
import FormActionsFooter from '../common/FormActionsFooter';
import ConfirmWordModal from '../common/ConfirmWordModal';
import RecordBadge from '../common/RecordBadge';
import { buildCardHierarchyOptions, linkedCardLabel } from '../cards/CardVisuals';
import { linkedLookupGroup, lookupScopeCards, lookupScopeCardIds, lookupTargetIds } from '../../utils/lookupScope';
import useLinkedSources from '../../hooks/useLinkedSources';
import { CardDefElements } from '../cards/CardDefinitionForm';
import { CARD_TAB, CARD_TAB_LABELS, useCardTab } from '../cards/cardTabs';
import { DataState, Skeleton, deriveDataState } from '../loading';
import ElementSetHome from './ElementSetHome';
import { checkStoredValues, destructiveElementEdits } from '../cards/hiddenValuesConfirm';
import { previewElementSetChange } from '../../services/cardDefs';
import { affectedSentences } from '../../utils/relatedLists';
import { escapeBelongsElsewhere } from '../../utils/escapeOwner';
import { useOptionalPreferences } from '../../contexts/PreferencesContext';
import { newMoneyCurrency } from '../../utils/newMoneyCurrency';
import { elementRefusalOf, elementRefusalText, ownerElementOfField, withElementChoice, withFieldPicks } from '../../utils/elementRefusal';
import useElementChoices from '../../hooks/useElementChoices';
import QueryRefusalPick from '../items/QueryRefusalPick';

/**
 * THE ELEMENT SET PAGE — `/element-sets/:id` (docs/plans/element-sets-and-me.md;
 * docs/platform-standards.md § The element set page), built from the card
 * page's parts: `/element-sets/:id` is the set's read-only HOME and `?tab=`
 * opens one editing domain over ONE draft with ONE Save — Basics (name,
 * description, icon and color; the key read-only once assigned), Elements
 * (the card editor's own rows, every stored name namespaced `<key>_<slug>`
 * and shown muted beneath the label; no driven value, no title element —
 * the card decides those) and a Danger zone whose Delete is behind the
 * typed word and answers a 409 `element_set_in_use` by naming the cards.
 *
 * Scope is the card's grammar: created from a collection's card library
 * (`?collectionId=`) the set belongs to that collection and its managers
 * edit it; an admin with no collection creates a GLOBAL set, which admins
 * edit and everyone may attach. A read-only viewer gets the home alone.
 */

// A set element may not be driven or calculated (the card decides those),
// so the editor never offers the control (the API refuses it too).
const SET_OPTION_KEYS_EXCLUDED = Object.freeze(['drivenFrom']);
// Nor may a set hold a "Me too" count (KPR-390): who confirmed is kept under
// the element's name on each item, which an extract would rename, and who may
// confirm is the card's collection's to say — so neither picker offers the
// type here (the API refuses one, 400 invalid_options).
// Nor a repeating group (KPR-679): a set is a shared definition whose extract,
// materialize and only-grows paths carry no columns, so the group is defined
// on the card (400 repeating_group_placement { where: 'elementSet' }).
const SET_TYPES_EXCLUDED = Object.freeze(['confirmations', 'repeating-group']);
const NO_CARDS = Object.freeze([]);
const NO_SETS = Object.freeze([]);

const createEmptySet = () => ({
	name: '',
	description: '',
	color: '',
	icon: '',
	elements: [],
	options: {},
});

const mapSetFromApi = (incoming = {}) => {
	// `usage` is an annotation, not part of the document the editor saves.
	const { usage, ...doc } = incoming || {};
	return {
		...createEmptySet(),
		...doc,
		description: doc?.description || '',
		color: doc?.color || '',
		icon: doc?.icon || '',
		elements: (doc?.elements || []).map(el => (el?.dataType ? el : { ...el, dataType: 'text-small' })),
		options: doc?.options || {},
	};
};

export default function ElementSetPage() {
	const { setId } = useParams();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const { message, modal, notification } = App.useApp();
	const isNewRecord = setId === 'new';
	const admin = isAdmin();
	const targetCollectionId = isNewRecord ? (searchParams.get('collectionId') || '') : '';
	const createBlocked = isNewRecord && !targetCollectionId && !admin;

	const [set, setSet] = useState(() => createEmptySet());
	const [savedSet, setSavedSet] = useState(null);
	const [usage, setUsage] = useState(null);
	const [loadFailed, setLoadFailed] = useState(false);
	const [loadAttempt, setLoadAttempt] = useState(0);
	const { isDirty, resetDirty, guardDiscard } = useDirtyForm(set);
	const resetDirtyRef = useRef(resetDirty);
	useEffect(() => { resetDirtyRef.current = resetDirty; }, [resetDirty]);
	const markPristine = useCallback(next => { resetDirtyRef.current(next); }, []);
	const editingLockedRef = useRef(false);

	const { cards = NO_CARDS, elementSets = NO_SETS, refreshElementSets, refresh } = useCardDefinitions();
	const refreshSets = refreshElementSets || refresh;

	// Load, or start blank. A stale response for an earlier id must not win.
	useEffect(() => {
		if (isNewRecord) {
			const fresh = createEmptySet();
			setSet(fresh);
			setSavedSet(null);
			setUsage(null);
			markPristine(fresh);
			return undefined;
		}
		let current = true;
		setLoadFailed(false);
		setSavedSet(null);
		api.get(API_ELEMENT_SET_URL(setId), { params: { include: 'usage' } })
			.then(({ data, status }) => {
				if (!current || status !== 200) return;
				const mapped = mapSetFromApi(data);
				setSet(mapped);
				setSavedSet(mapped);
				setUsage(data?.usage || null);
				markPristine(mapped);
			})
			.catch(() => { if (current) setLoadFailed(true); });
		return () => { current = false; };
	}, [isNewRecord, setId, markPristine, loadAttempt]);

	const currentId = normalizeId(savedSet?._id);
	const scope = isNewRecord ? (targetCollectionId ? 'collection' : (admin ? 'global' : null)) : (savedSet?.scope || null);
	const owningCollectionId = scope === 'collection' ? normalizeId(savedSet?.collection_id || targetCollectionId) : null;

	// The owning collection: the manage check and the breadcrumb.
	const [owningCollection, setOwningCollection] = useState(undefined);
	useEffect(() => {
		setOwningCollection(undefined);
		if (!owningCollectionId) return undefined;
		let mounted = true;
		api.get(`${API_COLLECTIONS_URL}/${owningCollectionId}`)
			.then(({ data, status }) => { if (mounted && status === 200) setOwningCollection(data || null); })
			.catch(() => { if (mounted) setOwningCollection(null); });
		return () => { mounted = false; };
	}, [owningCollectionId]);

	// The currency a new currency element starts in (ruled L3): the owning
	// collection's, else the author's own, else US dollars.
	const prefs = useOptionalPreferences()?.prefs || null;
	const newMoneyCode = useMemo(
		() => newMoneyCurrency({ collection: owningCollectionId ? owningCollection || null : null, prefs }),
		[owningCollectionId, owningCollection, prefs]
	);

	const readOnly = !isNewRecord && (
		(scope === 'global' && !admin)
		|| (scope === 'collection' && !admin && owningCollection !== undefined && !canManage(owningCollection))
	);
	const editingLocked = readOnly || createBlocked;
	editingLockedRef.current = editingLocked;
	useEffect(() => { if (editingLocked) markPristine(); }, [editingLocked, markPristine]);

	const handleChange = useCallback((path, value) => {
		if (editingLockedRef.current) return;
		setSet(prev => produce(prev, draft => { _.set(draft, path, value); }));
	}, []);

	// The namespace every element name is stored under: the set's key once
	// it has one, else the key the API will derive from its name — the slug,
	// suffixed past the keys its scope's siblings already hold (a collection's
	// own sets, or the global ones), as resolveKey does.
	const siblingKeys = useMemo(() => {
		if (!isNewRecord) return NO_SETS;
		const target = normalizeId(targetCollectionId);
		return (elementSets || [])
			.filter(sibling => (target
				? sibling?.scope === 'collection' && normalizeId(sibling?.collection_id) === target
				: sibling?.scope === 'global'))
			.map(sibling => sibling?.key)
			.filter(Boolean);
	}, [isNewRecord, elementSets, targetCollectionId]);
	const namespace = savedSet?.key || elementSetKeyFromName(set?.name, siblingKeys);
	const savedElements = useMemo(() => indexSavedElements(savedSet?.elements), [savedSet]);
	// A set's lookups obey the scope rule a card's do (utils/lookupScope.js):
	// the owning collection's cards, or global cards for a global set — plus
	// whatever the saved set already points at, flagged.
	// …and, for a collection's set, the cards a linked collection lends it
	// (keepr-api § Lookups across a link), in their own "Linked" group.
	const linkedTargets = useLinkedSources(owningCollectionId, { enabled: scope === 'collection' && Boolean(owningCollectionId) });
	const cardLookupOptions = useMemo(() => {
		const owner = { scope: scope || 'global', collection: owningCollection || null, collectionId: owningCollectionId };
		const inScope = lookupScopeCardIds(owner, cards);
		const linkedGroup = scope === 'collection' ? linkedLookupGroup(linkedTargets.borrowedCards, { labelFor: linkedCardLabel }) : null;
		const linkedIds = new Set((linkedGroup?.options || []).map(opt => opt.value));
		const local = buildCardHierarchyOptions(lookupScopeCards(owner, cards, lookupTargetIds(savedSet?.elements)))
			.filter(opt => !linkedIds.has(opt.value))
			.map(opt => (inScope.has(opt.value) ? opt : { ...opt, outOfScope: true }));
		return linkedGroup ? [...local, linkedGroup] : local;
	}, [scope, owningCollection, owningCollectionId, cards, savedSet, linkedTargets.borrowedCards]);

	// ---- Tabs: the card page's, minus Display (a set names no items) ----
	const canEdit = !editingLocked;
	const tabItems = [
		{ key: CARD_TAB.overview, label: CARD_TAB_LABELS.overview, disabled: isNewRecord },
		...(canEdit ? [
			{ key: CARD_TAB.basics, label: CARD_TAB_LABELS.basics },
			{ key: CARD_TAB.elements, label: CARD_TAB_LABELS.elements },
		] : []),
		...((canEdit && !isNewRecord && currentId) ? [{ key: CARD_TAB.danger, label: CARD_TAB_LABELS.danger }] : []),
	];
	const [activeTab, setTab] = useCardTab(
		tabItems.filter(tab => !tab.disabled).map(tab => tab.key),
		isNewRecord ? CARD_TAB.basics : CARD_TAB.overview
	);
	const onOverview = activeTab === CARD_TAB.overview;

	const pristine = useMemo(() => savedSet || createEmptySet(), [savedSet]);
	const dirtyTabs = useMemo(() => {
		const dirty = new Set();
		if (!isDirty) return dirty;
		const changed = (...paths) => paths.some(path => !_.isEqual(_.get(set, path), _.get(pristine, path)));
		if (changed('name', 'description', 'color', 'icon')) dirty.add(CARD_TAB.basics);
		if (changed('elements', 'options')) dirty.add(CARD_TAB.elements);
		return dirty;
	}, [isDirty, set, pristine]);

	// ---- Save ----
	const [saveStatus, setSaveStatus] = useState('idle');
	const [saveError, setSaveError] = useState(null);
	const untypedElements = useMemo(() => (set?.elements || [])
		.map((element, index) => ({ element, index }))
		.filter(({ element }) => !element?.dataType), [set?.elements]);

	// "Allow multiple" turned on or off over the carriers' stored values with
	// no answer on the element (owner ruling B16): the card editor's dialog,
	// here for a save that missed the switch-time question. Its answer
	// re-runs the save.
	const [shapeRefusal, setShapeRefusal] = useState(null);
	const [resavePending, setResavePending] = useState(false);

	const failSave = useCallback((coded) => {
		setSaveStatus('failed');
		setSaveError(coded);
		if (typeof coded?.element === 'string' || typeof coded?.index === 'number' || coded?.code === 'element_set_conflict') setTab(CARD_TAB.elements);
	}, [setTab]);

	const libraryPath = owningCollectionId
		? `/collections/${owningCollectionId}/cards`
		: '/cards';

	const savePayload = useMemo(() => {
		const body = {
			name: set?.name,
			description: set?.description || '',
			icon: set?.icon || '',
			color: set?.color || '',
			// "Allow multiple"'s answer travels only while it still applies (B16),
			// and a choice list on a non-Choice element only as it is stored
			// (withInertChoicesAsStored, KPE-033).
			elements: withInertChoicesAsStored(
				dropStaleShapeInstructions(set?.elements || [], savedElements, namespacedElementNames(set?.elements, namespace)),
				savedElements,
				namespacedElementNames(set?.elements, namespace),
			),
			options: set?.options || {},
		};
		if (!isNewRecord) return body;
		if (targetCollectionId) return { ...body, collection_id: targetCollectionId };
		return { ...body, scope: 'global' };
	}, [set, isNewRecord, targetCollectionId, savedElements, namespace]);

	// An element a lookup's picker filter on the set could mean several of
	// (400 `ambiguous_element`, element ids step 3): picked above the editor,
	// written into that filter's own text in what is sent (withFieldPicks, as
	// the card editor does) and sent as `elementChoices`. A pick answers
	// exactly the filters it was made for: any change forgets it.
	const filterTextsKey = useMemo(() => JSON.stringify((set?.elements || []).map(el => [el?.name || '', el?.options?.filter || ''])), [set?.elements]);
	const setPicks = useElementChoices(filterTextsKey);
	const fieldPicksRef = useRef({});
	useEffect(() => { fieldPicksRef.current = {}; }, [filterTextsKey]);
	const sendable = useCallback(payload => setPicks.withChoices(withFieldPicks(payload, fieldPicksRef.current)), [setPicks]);

	const saveSet = useCallback(async () => {
		if (!isDirty || editingLocked || saveStatus === 'saving') return;
		if (!(set?.name || '').trim()) {
			message.error('The element set was not saved: it needs a name.');
			setTab(CARD_TAB.basics);
			return;
		}
		if (untypedElements.length) {
			const first = untypedElements[0];
			const named = (first.element?.label?.singular || '').trim();
			failSave({ code: 'data_type_required', index: first.index, element: first.element?.name || null, message: 'Choose a data type' });
			message.error(`The element set was not saved: ${named ? `"${named}"` : `element ${first.index + 1}`} needs a data type.`);
			return;
		}
		// Removing or retyping one of the set's elements reaches every card
		// that carries it, and the values items hold under it stay where they
		// are — hidden, or unconverted (delete audit F9). The set's own
		// change-preview counts them across those cards; when any hold a
		// value, the danger confirm says so first.
		if (!isNewRecord && currentId) {
			const { removed, retyped } = destructiveElementEdits(savedSet?.elements, savePayload?.elements);
			if (removed.length || retyped.length) {
				const { proceed, refusal } = await checkStoredValues({
					modal,
					request: () => previewElementSetChange(currentId, sendable(savePayload)),
					labelOf: name => savedElements[name]?.label?.singular || name,
					where: "each card's Danger zone",
				});
				if (refusal) {
					failSave(refusal);
					// B16's question, or an element made an item lookup over held
					// values (KPR-1136, answered `clear`).
					const shape = shapeRefusalFor(refusal.details, refusal.element);
					if (shape) {
						setShapeRefusal(shape);
						return;
					}
					message.error(`The element set was not saved: ${refusal.message}`);
					return;
				}
				if (!proceed) return;
			}
		}
		setSaveStatus('saving');
		setSaveError(null);
		try {
			const response = isNewRecord
				? await api.post(API_ELEMENT_SETS_URL, sendable(savePayload))
				: await api.patch(API_ELEMENT_SET_URL(currentId), sendable(savePayload));
			setPicks.reset();
			fieldPicksRef.current = {};
			if (![200, 201].includes(response.status)) throw new Error(`Unexpected status ${response.status}`);
			message.success('Element set saved.');
			// A lookup a related list reads was removed, retyped or pointed
			// elsewhere by this save: the set saved, and each list that can no
			// longer find items says so (relatedListsAffected). The same notice
			// as the card editor's.
			const affectedLists = affectedSentences(response?.data?.relatedListsAffected);
			if (affectedLists.length) {
				notification.warning({
					message: 'Related lists affected',
					description: <ul className='mb-0'>{affectedLists.map(line => <li key={line}>{line}</li>)}</ul>,
					duration: 0,
				});
			}
			setSaveStatus('idle');
			const next = response?.data?.payload || response?.data;
			const newId = normalizeId(next?._id);
			if (next && newId) {
				const mapped = mapSetFromApi(next);
				setSet(mapped);
				setSavedSet(mapped);
				markPristine(mapped);
			} else {
				setSavedSet(set);
				markPristine();
			}
			refreshSets();
			if (isNewRecord && newId) navigate(`/element-sets/${newId}`, { replace: true });
		} catch (error) {
			const data = error?.response?.data;
			const shapeEntry = shapeRefusalFor(data, data?.element);
			if (shapeEntry) {
				failSave({ code: data.code, element: data.element || null, message: data.message || null });
				setShapeRefusal(shapeEntry);
				return;
			}
			// An element a lookup's picker filter could mean several of (or
			// none — element ids step 3) names the NAME the filter holds; the
			// row is the element its `field` names (`elements.<name>.options.filter`).
			// The pick is offered above the editor.
			setPicks.take(error);
			const filterRefusal = elementRefusalOf(data);
			const coded = data && typeof data.code === 'string'
				? {
					code: data.code,
					element: filterRefusal ? ownerElementOfField(data.field) : (data.element || null),
					message: filterRefusal ? (elementRefusalText(filterRefusal) || data.message || null) : (data.message || null),
					...(typeof data.index === 'number' ? { index: data.index } : {}),
					...(typeof data.dataType === 'string' ? { dataType: data.dataType } : {}),
					...(Array.isArray(data.cards) ? { cards: data.cards } : {}),
					...(data.conflictsWith ? { conflictsWith: data.conflictsWith } : {}),
				}
				: null;
			failSave(coded);
			const refusal = coded?.message || (error?.response?.status === 400 && typeof data?.message === 'string' ? data.message : null);
			message.error(refusal ? `The element set was not saved: ${refusal}` : 'Something went wrong. The element set may not have saved.');
		}
	}, [isDirty, editingLocked, saveStatus, set, untypedElements, failSave, message, notification, modal, setTab, isNewRecord, savePayload, currentId, savedSet, savedElements, markPristine, refreshSets, navigate, setPicks, sendable]);

	// "Allow multiple" switched on a SAVED set element (B16): the set's own
	// change-preview, with the saved set and only this element changed, says
	// whether the carriers' items hold values that need an answer first.
	const checkShapeChange = useCallback(async (name, nextElement) => {
		if (isNewRecord || !currentId || !Array.isArray(savedSet?.elements)) return null;
		if (!savedSet.elements.some(el => el?.name === name)) return null;
		const elements = savedSet.elements.map(el => (el?.name === name ? nextElement : el));
		try {
			const preview = await previewElementSetChange(currentId, { elements: dropStaleShapeInstructions(elements, savedElements, elements.map(el => el?.name)) });
			const refusal = preview?.wouldApply === false ? shapeRefusalFor(preview.refusal, name) : null;
			return refusal ? { refusal } : null;
		} catch (error) {
			return null;
		}
	}, [isNewRecord, currentId, savedSet, savedElements]);
	const convertShape = useCallback((conversion) => {
		const index = namespacedElementNames(set?.elements, namespace).indexOf(shapeRefusal?.element);
		setShapeRefusal(null);
		if (index < 0) return;
		// One answer joins another the element carries (`['first', 'detach']`,
		// the 10b contract).
		const element = set?.elements?.[index];
		const merged = mergeConversionAnswers(element?.options?.[SHAPE_INSTRUCTION_KEY], conversion, element, savedElements?.[shapeRefusal?.element] || null);
		handleChange(`elements[${index}].options.${SHAPE_INSTRUCTION_KEY}`, merged === undefined ? conversion : merged);
		setResavePending(true);
	}, [set?.elements, namespace, shapeRefusal, handleChange, savedElements]);
	useEffect(() => {
		if (!resavePending) return;
		setResavePending(false);
		saveSet();
	}, [resavePending, saveSet]);

	// ---- Delete (the Danger zone's typed word) ----
	const [confirmingDelete, setConfirmingDelete] = useState(false);
	const [deleteRefusal, setDeleteRefusal] = useState(null);
	const deleteSet = useCallback(async () => {
		if (!currentId) return;
		setDeleteRefusal(null);
		try {
			await api.delete(API_ELEMENT_SET_URL(currentId));
			message.success('Element set deleted.');
			markPristine();
			refreshSets();
			navigate(libraryPath);
		} catch (error) {
			const data = error?.response?.data;
			if (data?.code === 'element_set_in_use') {
				setDeleteRefusal({ cards: Array.isArray(data.cards) ? data.cards : [] });
				return;
			}
			message.error(data?.message || 'Delete failed.');
		}
	}, [currentId, message, markPristine, refreshSets, navigate, libraryPath]);

	// ---- Promote (admin: collection -> global, the key kept) ----
	const promoteSet = useCallback(() => {
		if (!currentId) return;
		modal.confirm({
			title: `Promote "${set?.name || 'this element set'}" to a global element set?`,
			content: 'Every keepr user will be able to attach it. Its key and every stored element name stay as they are. This cannot be undone from the app.',
			okText: 'Promote',
			onOk: async () => {
				try {
					const response = await api.post(API_ELEMENT_SET_PROMOTE_URL(currentId));
					const next = response?.data?.payload || response?.data;
					if (next?._id) {
						const mapped = mapSetFromApi(next);
						setSet(mapped);
						setSavedSet(mapped);
						markPristine(mapped);
					}
					message.success('Element set promoted to global.');
					refreshSets();
				} catch (error) {
					const data = error?.response?.data;
					message.error(data?.code === 'element_set_key_taken'
						? `A global element set already uses the key "${data.key || set?.key}".`
						: (data?.message || 'Promote failed.'));
				}
			},
		});
	}, [currentId, modal, set?.name, set?.key, markPristine, message, refreshSets]);

	const handleCancel = useCallback(() => {
		if (isNewRecord || !savedSet) {
			guardDiscard(() => navigate(libraryPath));
			return;
		}
		guardDiscard(() => {
			setSet(savedSet);
			markPristine(savedSet);
			setSaveStatus('idle');
			setSaveError(null);
			setTab(CARD_TAB.overview);
		}, { content: 'The element set goes back to how it was last saved.' });
	}, [isNewRecord, savedSet, navigate, libraryPath, markPristine, guardDiscard, setTab]);

	useScreenShortcuts('Element set editor', [
		{ keys: ['mod', 'enter'], label: 'Save the element set' },
		{ keys: ['esc'], label: 'Cancel' },
	]);
	const shortcutRef = useRef(null);
	shortcutRef.current = { saveSet, handleCancel };
	useEffect(() => {
		const handler = (event) => {
			const current = shortcutRef.current;
			if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
				event.preventDefault();
				current.saveSet();
			} else if (event.key === 'Escape') {
				if (escapeBelongsElsewhere(event)) return;
				event.preventDefault();
				current.handleCancel();
			}
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, []);

	// ---- Chrome ----
	const scopeTag = scope === 'global'
		? <Tag color='blue'>Global</Tag>
		: (scope === 'collection' ? <Tag color='purple'>Collection element set</Tag> : null);
	const scopeText = (() => {
		if (createBlocked) return null;
		if (isNewRecord && targetCollectionId) return 'This element set will belong to the collection you came from. Only that collection\'s cards can attach it.';
		if (isNewRecord && admin) return 'Creating a global element set. Any card on keepr will be able to attach it.';
		if (scope === 'global') return admin ? 'Provided by keepr for everyone. Edits here reach every card that attaches it.' : 'Provided by keepr and read-only. Any card can attach it.';
		if (scope === 'collection') return readOnly ? 'Owned by a collection you don\'t manage. Read-only.' : 'Owned and managed by this collection. Only its cards can attach it.';
		return null;
	})();

	const headerActions = [
		owningCollection ? {
			key: 'collection', group: 'navigate', glyph: 'card', label: 'Collection cards',
			onClick: () => navigate(`/collections/${normalizeId(owningCollection._id)}/cards`),
		} : null,
		{ key: 'cards', group: 'navigate', glyph: 'items', label: 'All cards', onClick: () => navigate('/cards') },
		(admin && scope === 'collection' && currentId) ? {
			key: 'promote', group: 'record', glyph: 'collection', label: 'Promote to global', onClick: promoteSet,
		} : null,
		(!isNewRecord && !editingLocked && currentId) ? {
			key: 'delete', group: 'destructive', glyph: 'delete', label: 'Delete element set…', onClick: () => setTab(CARD_TAB.danger),
		} : null,
	];

	const visual = { icon: set?.icon, color: set?.color, name: set?.name };
	const displayName = (set?.name || '').trim() || (isNewRecord ? 'New element set' : 'Untitled element set');
	const usedByCards = Array.isArray(usage?.cards) ? usage.cards : [];

	const formActions = editingLocked ? null : (
		<>
			<Button onClick={handleCancel}>Cancel</Button>
			<Button type='primary' onClick={saveSet} loading={saveStatus === 'saving'} disabled={!isDirty || saveStatus === 'saving'}>
				{saveStatus === 'failed' ? 'Retry save' : (isNewRecord ? 'Create element set' : 'Save changes')}
			</Button>
		</>
	);

	// A set edit the API refused because a card carrying the set would then
	// collide (409 element_set_conflict with `cards`), in the section's words.
	const conflictAlert = saveError?.code === 'element_set_conflict' ? (
		<Alert
			className='card-element-sets__error'
			type='error'
			showIcon
			message={`"${saveError.element || 'An element'}" would collide with ${saveError.conflictsWith?.name ? `"${saveError.conflictsWith.name}"` : 'another definition'}${saveError.cards?.length ? ` on ${saveError.cards.map(card => card?.name).filter(Boolean).join(', ')}` : ''}. Nothing was saved.`}
		/>
	) : null;

	// An existing set that has not answered yet (or failed to): a skeleton or an error with Retry,
	// never an editable blank draft that a person could type into and save over the real set.
	const awaitingSet = !isNewRecord && !savedSet;

	return <>
		<div className='item-form-page card-def-form element-set-page'>
			<ContextBar
				breadcrumb={owningCollection
					? collectionCrumbs(owningCollection, owningCollectionId, 'Element set')
					: [CARDS_CRUMB, { label: 'Element set' }]}
				record={visual}
				kind='elementSet'
				title={displayName}
				actions={(
					<>
						{!onOverview && !editingLocked && !isNewRecord && !isDirty && saveStatus !== 'saving' ? (
							<span className='item-form-draft' data-testid='no-changes'>No changes</span>
						) : null}
						{onOverview ? null : formActions}
					</>
				)}
				menuActions={headerActions}
				menuLabel='Element set actions'
			>
				{tabItems.length > 1 ? (
					<ContextBarTabs
						aria-label='Element set sections'
						items={tabItems.map(tab => ({
							key: tab.key,
							disabled: tab.disabled,
							label: dirtyTabs.has(tab.key) ? (
								<>
									{tab.label}
									<span className='context-tabs__dot' aria-hidden='true' />
									<span className='visually-hidden'> (unsaved changes)</span>
								</>
							) : tab.label,
						}))}
						activeKey={activeTab}
						onChange={setTab}
					/>
				) : null}
			</ContextBar>

			{createBlocked ? (
				<Alert
					type='warning'
					showIcon
					className='card-def-form__alert'
					message='Element sets are created from inside a collection'
					description="Open the collection's Cards tab and use “Create an element set” in its ⋯. The new set will belong to that collection."
				/>
			) : null}
			{awaitingSet ? (
				<DataState
					state={deriveDataState({ loading: !loadFailed, error: loadFailed ? true : null, hasData: false })}
					skeleton={<Skeleton variant='form-field' count={4} />}
					onRetry={() => setLoadAttempt(n => n + 1)}
					label='this element set'
				/>
			) : (
			<div role='tabpanel' className='settings-tabpanel'>
				{setPicks.refusal ? (
					<div className='card-def-form__filter-pick'>
						<QueryRefusalPick
							inline
							refusal={setPicks.refusal}
							disabled={saveStatus === 'saving'}
							onPick={(value) => {
								const refusal = setPicks.refusal;
								if (refusal?.field) {
									fieldPicksRef.current = {
										...fieldPicksRef.current,
										[refusal.field]: withElementChoice(fieldPicksRef.current[refusal.field] || {}, refusal, value),
									};
								}
								setPicks.pick(value);
								saveSet();
							}}
							onRepick={() => { fieldPicksRef.current = {}; setPicks.clearRefusal(); saveSet(); }}
						/>
					</div>
				) : null}
				{onOverview ? (savedSet ? (
					<ElementSetHome set={savedSet} usage={usage} scopeTag={scopeTag} editable={canEdit} />
				) : null) : activeTab === CARD_TAB.danger ? (
					<div className='settings-column'>
						{deleteRefusal ? (
							<Alert
								type='error'
								showIcon
								message='This element set is still attached to cards'
								description={(
									<div className='card-home__badges'>
										{deleteRefusal.cards.map(card => (
											<RecordBadge key={normalizeId(card?._id)} kind='card' record={card} title={card?.name || '(untitled card)'} size='sm' to={`/cards/${normalizeId(card?._id)}`} />
										))}
										<span className='form-panel__hint'>Remove it from each card (or copy it into the card) first.</span>
									</div>
								)}
							/>
						) : null}
						<section className='form-panel form-panel--danger'>
							<div className='danger-row'>
								<div className='toggle-row__text'>
									<span className='toggle-row__label'>Delete this element set</span>
									<span className='toggle-row__description'>
										Refused while any card still attaches it. Values already stored on items are not deleted: they stay on the items, hidden.
									</span>
								</div>
								<Button danger onClick={() => setConfirmingDelete(true)}>Delete element set…</Button>
							</div>
						</section>
					</div>
				) : (
					<div className='item-form-columns'>
						<fieldset disabled={editingLocked} className='item-form-main card-def-form__main'>
							{activeTab === CARD_TAB.basics ? <>
								<section className='form-panel'>
									<div className='micro-label form-panel__kicker'>Basics</div>
									<InputText elementName='name' elementLabel='Name' value={set?.name} onChange={handleChange} required={true} />
									<InputText elementName='description' elementLabel='Description' value={set?.description} onChange={handleChange} />
									<p className='form-panel__hint'>
										{savedSet?.key
											? `Key: ${savedSet.key}. Fixed once the set is created; every element is stored as ${savedSet.key}_<name>.`
											: `Key: ${namespace}. Derived from the name, fixed once the set is created; every element will be stored as ${namespace}_<name>.`}
									</p>
								</section>
								<section className='form-panel'>
									<div className='micro-label form-panel__kicker'>Identity</div>
									<InputIconSelect
										elementName='icon'
										elementLabel='Icon and color'
										value={set?.icon}
										onChange={handleChange}
										colorElementName='color'
										colorValue={set?.color}
										onColorChange={handleChange}
										recordKind='elementSet'
									/>
								</section>
							</> : null}

							{activeTab === CARD_TAB.elements ? (
								<section className='form-panel'>
									{conflictAlert}
									<CardDefElements
										elements={set?.elements || []}
										onChange={handleChange}
										newMoneyCode={newMoneyCode}
										showTitle={true}
										cardOptions={cardLookupOptions}
										collectionCards={cards}
										saveError={saveError}
										savedElements={savedElements}
										namespace={namespace}
										optionKeysExcluded={SET_OPTION_KEYS_EXCLUDED}
										typesExcluded={SET_TYPES_EXCLUDED}
										onCheckShapeChange={checkShapeChange}
									/>
									<MultiToggleDialog
										open={Boolean(shapeRefusal)}
										refusal={shapeRefusal}
										label={savedElements[shapeRefusal?.element]?.label?.singular || shapeRefusal?.element}
										collectionId={owningCollectionId}
										onConvert={convertShape}
										onCancel={() => setShapeRefusal(null)}
									/>
								</section>
							) : null}

							<FormActionsFooter>{formActions}</FormActionsFooter>
						</fieldset>

						<aside className='item-form-side'>
							<section className='form-panel'>
								<div className='micro-label form-panel__kicker'>Scope</div>
								<div className='card-def-form__scope'>
									{scopeTag}
									{scopeText ? <p>{scopeText}</p> : null}
								</div>
							</section>
							<section className='form-panel'>
								<div className='micro-label form-panel__kicker'>
									Used by
									{usedByCards.length ? <span className='form-panel__count'>{usedByCards.length}</span> : null}
								</div>
								{currentId ? (usedByCards.length ? (
									<div className='card-def-form__children'>
										{usedByCards.map(card => (
											<RecordBadge key={normalizeId(card?._id)} kind='card' record={card} title={card?.name || '(untitled card)'} size='sm' to={`/cards/${normalizeId(card?._id)}`} />
										))}
									</div>
								) : (
									<p className='form-panel__hint'>No card attaches this set yet.</p>
								)) : (
									<p className='form-panel__hint'>Save this element set, then attach it from a card\'s Elements tab.</p>
								)}
							</section>
							<div className='item-form-shortcuts'>
								<span><kbd>⌘↵</kbd> Save</span>
								<span><kbd>Esc</kbd> Cancel</span>
							</div>
						</aside>
					</div>
				)}
			</div>
			)}
		</div>

		<ConfirmWordModal
			open={confirmingDelete}
			word='delete'
			title='Delete this element set?'
			okText='Delete'
			onOk={() => { setConfirmingDelete(false); deleteSet(); }}
			onCancel={() => setConfirmingDelete(false)}
		>
			<p>
				Deleting <strong>{displayName}</strong> is refused while any card still attaches it.
				Values already stored on items are not deleted: they stay on the items, hidden.
			</p>
		</ConfirmWordModal>
	</>;
}
